import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  CalendarIcon,
  DrawingPinIcon,
  FileIcon,
  GearIcon,
  GridIcon,
  Link2Icon,
} from '@radix-ui/react-icons';
import { OverviewIcon } from '@producer/ui-kit';
import styled from 'styled-components';

import { NavMenu, NavMenuItem } from '../../../components/nav-menu';
import { uiStore } from '../../../core/stores/ui-store';
import { useCurrentProject } from '../../../hooks/use-current-project';
import { projectPath, projectTabPath } from '../../../app/paths';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import {
  ClipboardTick,
  MaximizeMenuIcon,
  MinimizeMenuIcon,
  TheatreMasksIcon,
} from '../../../components/icons';

type ProjectMenuProps = {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  onSelect?: () => void;
};

export const ProjectMenu: React.FC<ProjectMenuProps> = observer(
  ({ isOpen, setIsOpen, onSelect }) => {
    const project = useCurrentProject();

    if (!project) {
      return null;
    }

    return (
      <Container data-expand={isOpen}>
        <MenuContent>
          <NavMenu>
            <Tooltip text="Overview" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectPath(project.spaceId, project._id)}
                icon={<OverviewIcon />}
                label="Overview"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.overview'}
              />
            </Tooltip>

            <Tooltip text="Documents" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'documents')}
                icon={<FileIcon />}
                label="Documents"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.documents'}
              />
            </Tooltip>

            <Tooltip text="Tasks" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'tasks')}
                icon={<ClipboardTick />}
                label="Tasks"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.tasks'}
              />
            </Tooltip>

            <Tooltip text="Calendar" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'calendar')}
                icon={<CalendarIcon />}
                label="Calendar"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.calendar'}
              />
            </Tooltip>

            <Tooltip text="Shared Links" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'share-links')}
                icon={<Link2Icon />}
                label="Shared Links"
                isActive={uiStore.currentLocation === 'project.share-links'}
                onClick={onSelect}
              />
            </Tooltip>

            <Tooltip text="Settings" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'settings')}
                icon={<GearIcon />}
                label="Settings"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.settings'}
              />
            </Tooltip>

            <Subtitle data-expand={isOpen}>Creation</Subtitle>

            <Tooltip text="Storyboards" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'storyboards')}
                icon={<GridIcon />}
                label="Storyboards"
                onClick={onSelect}
                isActive={uiStore.currentLocation === 'project.storyboards'}
              />
            </Tooltip>

            <Subtitle data-expand={isOpen}>Resources</Subtitle>

            <Tooltip text="Locations" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'locations')}
                icon={<DrawingPinIcon />}
                label="Locations"
                isActive={uiStore.currentLocation === 'project.locations'}
                onClick={onSelect}
              />
            </Tooltip>

            <Tooltip text="Characters" position="right" disabled={isOpen}>
              <NavMenuItem
                to={projectTabPath(project.spaceId, project._id, 'characters')}
                icon={<TheatreMasksIcon />}
                label="Characters"
                isActive={uiStore.currentLocation === 'project.characters'}
                onClick={onSelect}
              />
            </Tooltip>
          </NavMenu>
        </MenuContent>

        <div style={{ flex: 1 }} />

        <Footer>
          {setIsOpen && (
            <MenuToggleButton onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <MinimizeMenuIcon /> : <MaximizeMenuIcon />}
            </MenuToggleButton>
          )}
        </Footer>
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 4rem;
  min-width: 4rem;
  position: relative;
  transition: all 0.2s ease;
  border-right: 1px var(--content-border-color) solid;

  &[data-expand='true'] {
    width: 20rem;
    min-width: 20rem;
  }
`;

const MenuContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const Subtitle = styled.div`
  margin-top: 0.8rem;
  font-size: 1.3rem;
  height: 2.6rem;
  color: transparent;
  display: flex;
  align-items: center;
  padding: 0.4rem;

  &:after {
    content: '';
    position: absolute;
    transform: translateX(-0.8rem);
    height: 1px;
    width: 100%;
    background: var(--color-surfaces-bg-elevation-2);
  }

  &[data-expand='true'] {
    color: #7a8296;

    &:after {
      display: none;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 0.4rem;
`;

const MenuToggleButton = styled.button`
  all: unset;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: var(--border-radius-small);
  background: var(--color-surfaces-app-background-0);
  color: var(--color-texts-low-contrast);
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;
    color: var(--color-texts-high-contrast);
    background: var(--bg-elevation-2);
  }
`;
